/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import SellerRatingModal from '../../components/seller/SellerRatingModal';
import { NextEvaluationCard, markEvalDone } from '../../components/seller/NextEvaluationCard';
import { formatCurrency, formatDate, MANDATE_STATUS_LABELS, getStatusColor } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import MandateTerminationModal from '../../components/seller/MandateTerminationModal';
import { VisitReportsPanel } from '../../components/visits/VisitReportsPanel';
import SellerTimeline from '../../components/seller/SellerTimeline';
import SellerActivityTimeline from '../../components/engagement/SellerActivityTimeline';
import SellerVisitCard from '../../components/seller/SellerVisitCard';
import SellerAIChat from '../../components/seller/SellerAIChat';
import { PageHeader, EmptyState, KPIWithExplanation } from '../../components/homly';
import {
    FileText, Home, Clock, CheckCircle, MessageCircle,
    Building2, Ban, Users, MapPin,
    Shield, Lock, Phone, Mail, TrendingUp, Eye,
    Calendar, Activity, CalendarCheck, UserCheck, ThumbsUp,
    CircleDot
} from 'lucide-react';

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function SellerDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedMandate, setSelectedMandate] = useState(null);
    const [showTerminationModal, setShowTerminationModal] = useState(false);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [ratingMandate, setRatingMandate] = useState(null);

    const loadData = useCallback(async () => {
        try {
            const res = await api.get('/mandates/seller/dashboard-data');
            setData(res.data);
        } catch {
            try {
                const res = await api.get('/mandates/');
                setData({
                    mandates: res.data || [], activities: [], visits: { all: [], upcoming: [], completed: [], stats: { total: 0, upcoming: 0, completed: 0, interested: 0 } },
                    stats: { total_mandates: (res.data || []).length, active_mandates: 0, sold: 0, agencies_count: 0, total_visits: 0, upcoming_visits: 0 },
                    exclusivity: { has_active_exclusive: false }
                });
            } catch { toast.error('Erreur de chargement'); }
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const canTerminate = (m) => !['vendu', 'sold', 'denonce', 'denounced', 'expire', 'expired'].includes(m.status);

    const groupByProperty = (mandates) => {
        const map = new Map();
        (mandates || []).forEach(m => {
            const addr = m.property?.address || 'Bien';
            const city = m.property?.city || '';
            const key = `${addr}-${city}`.toLowerCase();
            if (!map.has(key)) map.set(key, { address: addr, city, price: m.pricing?.price_hai || m.price, type: m.property?.property_type, surface: m.property?.surface_habitable || m.property?.surface, mandates: [] });
            map.get(key).mandates.push(m);
        });
        return Array.from(map.values());
    };

    if (loading) {
        return (
            <div className="max-w-5xl mx-auto space-y-6 p-4">
                <Skeleton className="h-24 w-full bg-muted rounded-2xl" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 bg-muted rounded-xl" />)}</div>
                <Skeleton className="h-64 w-full bg-muted rounded-2xl" />
            </div>
        );
    }

    const { mandates = [], activities = [], visits = {}, stats = {}, exclusivity = {}, visit_reports: visitReports = {} } = data || {};
    const properties = groupByProperty(mandates);
    const upcomingVisits = visits.upcoming || [];
    const completedVisits = visits.completed || [];
    const visitStats = visits.stats || {};
    const vrStats = visitReports.stats || {};

    return (
        <motion.div className="max-w-6xl mx-auto space-y-4 sm:space-y-6" initial="hidden" animate="visible" variants={stagger} data-testid="seller-dashboard">

            {/* Header Dark Luxury — rassurant */}
            <motion.div variants={fadeIn}>
                <PageHeader
                    kicker={`Bonjour ${user?.first_name || ''}, voici l'avancement de votre vente`}
                    title={
                        properties.length > 1
                            ? `${properties.length} biens suivis pas à pas`
                            : stats.active_mandates > 0
                                ? 'Votre bien est entre de bonnes mains'
                                : 'Bienvenue sur votre espace vendeur'
                    }
                    subtitle={
                        stats.upcoming_visits > 0
                            ? `${stats.upcoming_visits} visite${stats.upcoming_visits > 1 ? 's' : ''} programmée${stats.upcoming_visits > 1 ? 's' : ''} · ${stats.agencies_count || 1} agence${stats.agencies_count > 1 ? 's' : ''} active${stats.agencies_count > 1 ? 's' : ''}. Pas besoin d'appeler, tout est ici.`
                            : "Vous verrez ici en temps réel chaque action de votre agent : visites, retours, offres. Pas besoin d'appeler pour savoir où ça en est."
                    }
                    primaryAction={{
                        label: 'Aide & Support',
                        icon: MessageCircle,
                        onClick: () => { window.location.href = '/seller/support'; },
                    }}
                    dataTestid="seller-cockpit-header"
                />
            </motion.div>

            {/* KPIs Dark Luxury avec interprétation */}
            <motion.div variants={fadeIn}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 14,
                    }}
                >
                    <KPIWithExplanation
                        label="Biens en vente"
                        value={stats.active_mandates || 0}
                        icon={Home}
                        accent="purple"
                        explanation={
                            stats.active_mandates > 0
                                ? `${stats.active_mandates} mandat${stats.active_mandates > 1 ? 's' : ''} en cours de commercialisation par votre/vos agent(s).`
                                : 'Aucun mandat actif pour le moment.'
                        }
                        dataTestid="kpi-biens"
                    />
                    <KPIWithExplanation
                        label="Visites · total"
                        value={stats.total_visits || 0}
                        icon={CalendarCheck}
                        accent="orange"
                        explanation={
                            stats.total_visits > 0
                                ? `${vrStats.interested || visitStats.interested || 0} visiteur${(vrStats.interested || visitStats.interested || 0) > 1 ? 's' : ''} se sont déclarés intéressés.`
                                : 'Aucune visite encore organisée.'
                        }
                        dataTestid="kpi-visites"
                    />
                    <KPIWithExplanation
                        label="Visites à venir"
                        value={stats.upcoming_visits || 0}
                        icon={Calendar}
                        accent={stats.upcoming_visits > 0 ? 'purple' : 'gray'}
                        explanation={
                            stats.upcoming_visits > 0
                                ? `Détails et horaires juste en dessous.`
                                : 'Aucune visite planifiée cette semaine.'
                        }
                        dataTestid="kpi-upcoming"
                    />
                    <KPIWithExplanation
                        label="Biens vendus"
                        value={stats.sold || 0}
                        icon={CheckCircle}
                        accent={stats.sold > 0 ? 'green' : 'gray'}
                        explanation={
                            stats.sold > 0
                                ? `Félicitations ! ${stats.sold} bien${stats.sold > 1 ? 's ont' : ' a'} déjà été vendu${stats.sold > 1 ? 's' : ''}.`
                                : 'La vente est en cours, gardez confiance.'
                        }
                        dataTestid="kpi-vendus"
                    />
                </div>
            </motion.div>

            {/* Upcoming visits */}
            {upcomingVisits.length > 0 && (
                <motion.div variants={fadeIn}>
                    <div className="bg-card rounded-2xl border border-[#681793]/20 overflow-hidden">
                        <div className="p-4 sm:p-5 border-b border-border bg-gradient-to-r from-[#681793]/5 to-transparent">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-lg bg-[#681793]/20 flex items-center justify-center">
                                        <CalendarCheck className="w-4 h-4 text-[#681793]" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground text-sm">Prochaines visites</h3>
                                        <p className="text-xs text-muted-foreground">{upcomingVisits.length} visite(s) programmée(s)</p>
                                    </div>
                                </div>
                                <Badge className="bg-[#681793]/10 text-[#681793] border-[#681793]/20 text-xs">
                                    <CircleDot className="w-3 h-3 mr-1 animate-pulse" /> En cours
                                </Badge>
                            </div>
                        </div>
                        <div className="p-3 sm:p-4 space-y-2">
                            {upcomingVisits.slice(0, 5).map(visit => <SellerVisitCard key={visit.id} visit={visit} />)}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Reassurance banner */}
            <motion.div variants={fadeIn}>
                <div className="bg-gradient-to-r from-[#681793]/10 via-[#FF914D]/5 to-[#681793]/10 rounded-2xl border border-[#681793]/15 p-4 sm:p-5">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#681793]/20 flex items-center justify-center shrink-0">
                            <Shield className="w-5 h-5 text-[#681793]" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-foreground text-sm">Votre bien est entre de bonnes mains</h3>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <TrendingUp className="w-3.5 h-3.5 text-green-400 shrink-0" />
                                    <span><strong className="text-foreground">{vrStats.total || visitStats.total || 0}</strong> retour(s) de visite</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <ThumbsUp className="w-3.5 h-3.5 text-[#FF914D] shrink-0" />
                                    <span><strong className="text-foreground">{vrStats.interested || visitStats.interested || 0}</strong> visiteur(s) intéressé(s)</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Users className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                    <span><strong className="text-foreground">{stats.agencies_count || 0}</strong> agence(s) active(s)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Exclusivity warning */}
            {exclusivity?.has_active_exclusive && (
                <motion.div variants={fadeIn}>
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-semibold text-amber-300 text-sm">Mandat exclusif en cours</p>
                            <p className="text-amber-300/70 text-xs mt-1">
                                Vous avez un mandat exclusif actif. Pendant sa durée, aucune autre agence ne peut être mandatée pour ce bien.
                                {exclusivity.exclusive_end_date && <> Fin prévue : <strong>{formatDate(exclusivity.exclusive_end_date)}</strong></>}
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Properties with mandates */}
            {properties.length > 0 ? (
                properties.map((property, pIdx) => (
                    <motion.div key={pIdx} variants={fadeIn}>
                        <div className="bg-card rounded-2xl border border-border overflow-hidden">
                            {/* Property header */}
                            <div className="bg-gradient-to-r from-[#681793]/10 to-[#FF914D]/10 p-5 border-b border-border">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                                            <Home className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <MapPin className="w-3 h-3 text-muted-foreground" />
                                                <p className="font-bold text-foreground">{property.address}</p>
                                            </div>
                                            <p className="text-sm text-muted-foreground">{property.city}</p>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-lg font-bold text-[#FF914D]">{formatCurrency(property.price)}</span>
                                                {property.surface && <span className="text-xs text-muted-foreground">{property.surface} m²</span>}
                                            </div>
                                        </div>
                                    </div>
                                    {property.mandates.length > 1 && (
                                        <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-xs"><Users className="w-3 h-3 mr-1" />{property.mandates.length} agences</Badge>
                                    )}
                                </div>
                            </div>

                            {/* Mandates */}
                            <div className="divide-y divide-border">
                                {property.mandates.map(mandate => (
                                    <div key={mandate.id} className="p-5 space-y-4" data-testid={`seller-mandate-${mandate.id}`}>
                                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center shrink-0">
                                                    <Building2 className="w-5 h-5 text-muted-foreground" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="font-semibold text-foreground">{mandate.agency_name}</p>
                                                        <Badge className={`${getStatusColor(mandate.status)} border-0 text-[10px] px-1.5 py-0`}>{MANDATE_STATUS_LABELS[mandate.status] || mandate.status}</Badge>
                                                        {mandate.mandate_type === 'exclusif' && (
                                                            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] px-1.5 py-0"><Shield className="w-3 h-3 mr-0.5" />Exclusif</Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                                                        {mandate.mandate_number && <span>N° {mandate.mandate_number}</span>}
                                                        <span>Agent : {mandate.agent_name}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {mandate.agent_phone && <a href={`tel:${mandate.agent_phone}`}><Button variant="outline" size="sm" className="border-border h-8 px-2 lg:px-3" data-testid={`call-agent-${mandate.id}`}><Phone className="w-3.5 h-3.5 lg:mr-1.5" /><span className="hidden lg:inline text-xs">Appeler</span></Button></a>}
                                                {mandate.agent_email && <a href={`mailto:${mandate.agent_email}`}><Button variant="outline" size="sm" className="border-border h-8 px-2 lg:px-3" data-testid={`email-agent-${mandate.id}`}><Mail className="w-3.5 h-3.5 lg:mr-1.5" /><span className="hidden lg:inline text-xs">Email</span></Button></a>}
                                                <Link to={`/seller/mandates/${mandate.id}`}><Button variant="outline" size="sm" className="border-border h-8 text-xs"><Eye className="w-3.5 h-3.5 mr-1" />Détails</Button></Link>
                                                {canTerminate(mandate) && <Button variant="outline" size="sm" onClick={() => { setSelectedMandate(mandate); setShowTerminationModal(true); }} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs" data-testid={`terminate-mandate-${mandate.id}`}><Ban className="w-3.5 h-3.5 mr-1" />Dénoncer</Button>}
                                            </div>
                                        </div>
                                        <SellerTimeline status={mandate.status} />
                                        <SellerActivityTimeline mandateId={mandate.id} />
                                        <div className="flex items-center gap-4 lg:gap-6 text-xs text-muted-foreground flex-wrap">
                                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Signé le {formatDate(mandate.signed_at || mandate.start_date || mandate.created_at)}</span>
                                            {mandate.end_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Fin : {formatDate(mandate.end_date)}</span>}
                                            <span>Durée : {mandate.duration_months || 3} mois</span>
                                        </div>
                                        <div className="mt-2 pt-3 border-t border-border">
                                            <VisitReportsPanel mandateId={mandate.id} isSeller={true} isDark={true} />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {property.mandates.length > 1 && property.mandates.every(m => m.mandate_type === 'simple') && (
                                <div className="px-5 pb-5">
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 flex items-start gap-2">
                                        <Users className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                                        <p className="text-xs text-blue-300/80"><strong className="text-blue-300">Mandat simple — Multi-agences.</strong> Vous avez confié ce bien à {property.mandates.length} agences.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                ))
            ) : (
                <motion.div variants={fadeIn} data-testid="seller-empty-mandates">
                    <div
                        style={{
                            background: 'var(--homly-bg-elevated)',
                            border: '1px solid var(--homly-border)',
                            borderRadius: 'var(--homly-radius-lg)',
                        }}
                    >
                        <EmptyState
                            icon={Home}
                            title="Votre espace est prêt — en attente de votre premier mandat"
                            description="Dès que votre agent immobilier créera un mandat de vente, vous le verrez apparaître ici en temps réel : visites, retours, offres, sentiment de l'acheteur. Vous n'aurez plus besoin d'appeler pour savoir où ça en est."
                            secondaryAction={{
                                label: 'Contacter mon agent',
                                onClick: () => { window.location.href = '/seller/support'; },
                            }}
                            accent="purple"
                        />
                    </div>
                </motion.div>
            )}

            {/* Completed visits history */}
            {completedVisits.length > 0 && (
                <motion.div variants={fadeIn}>
                    <div className="bg-card rounded-2xl border border-border overflow-hidden">
                        <div className="p-4 sm:p-5 border-b border-border">
                            <div className="flex items-center gap-2">
                                <UserCheck className="w-4 h-4 text-green-400" />
                                <h3 className="font-semibold text-foreground text-sm">Historique des visites</h3>
                                <Badge className="bg-muted text-muted-foreground text-xs border-0 ml-auto">{completedVisits.length} effectuée(s)</Badge>
                            </div>
                        </div>
                        <div className="p-3 sm:p-4 space-y-2">
                            {completedVisits.slice(0, 8).map(visit => <SellerVisitCard key={visit.id} visit={visit} />)}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Recent activity */}
            {activities.length > 0 && (
                <motion.div variants={fadeIn}>
                    <div className="bg-card rounded-2xl border border-border overflow-hidden">
                        <div className="p-5 border-b border-border">
                            <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-[#FF914D]" />
                                <h3 className="font-semibold text-foreground">Activité récente</h3>
                            </div>
                        </div>
                        <div className="divide-y divide-border">
                            {activities.slice(0, 5).map((act, idx) => (
                                <div key={act.id || idx} className="px-5 py-3 flex items-center gap-3">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${act.is_read ? 'bg-muted-foreground' : 'bg-[#FF914D]'}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-foreground truncate">{act.title}</p>
                                        <p className="text-xs text-muted-foreground truncate">{act.message}</p>
                                    </div>
                                    <span className="text-xs text-muted-foreground flex-shrink-0">{formatDate(act.created_at)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Help cards */}
            <motion.div variants={fadeIn} className="grid sm:grid-cols-2 gap-3">
                <Link to="/seller/support">
                    <div className="bg-card rounded-xl border border-border p-5 hover:bg-muted hover:border-white/20 transition-all group h-full">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#FF914D]/10 flex items-center justify-center group-hover:scale-105 transition-transform">
                                <MessageCircle className="w-5 h-5 text-[#FF914D]" />
                            </div>
                            <div>
                                <p className="font-semibold text-foreground text-sm">Aide & Support</p>
                                <p className="text-xs text-muted-foreground">Questions fréquentes et contact</p>
                            </div>
                        </div>
                    </div>
                </Link>
                <NextEvaluationCard
                    mandates={mandates}
                    onEvaluate={(m) => { setRatingMandate(m); setShowRatingModal(true); }}
                />
            </motion.div>

            {/* Modals */}
            <MandateTerminationModal isOpen={showTerminationModal} onClose={() => { setShowTerminationModal(false); setSelectedMandate(null); }} mandate={selectedMandate} userId={user?.id} onTerminationComplete={() => { toast.success('Dénonciation enregistrée'); loadData(); }} />
            <SellerRatingModal isOpen={showRatingModal} onClose={() => { setShowRatingModal(false); setRatingMandate(null); }} mandate={ratingMandate} agentName={ratingMandate?.agent_name} ratingStep="mensuel" onComplete={() => { markEvalDone(ratingMandate); loadData(); }} />

            {/* AI Chat — floating assistant (replaces support tickets for non-sensitive questions) */}
            <SellerAIChat />

            {/* GuidedTour & HelpButton are mounted globally in MainLayout — do not duplicate here */}
        </motion.div>
    );
}

export default SellerDashboard;
